import localStorage from 'utils/localStorage'
import { readCookie } from 'utils/cookie'

const MAX_CACHE_AGE = 24 * 60 * 60 * 1000

export const getPermissions = (id = '', { includeStale = true } = {}) => {
  const cache = includeStale ? null : MAX_CACHE_AGE
  const cached = localStorage.readValue('userConsents', cache)

  if (cached && (!id || cached.id === id)) {
    return cached.permissions
  }

  return null
}

const loadPermissions = (consentUrl, options) => {
  return fetch(consentUrl, options).then((r) => {
    if (r.status === 203) {
      return Promise.reject(new Error('GUD Identity is unavailable'))
    }

    return r.json()
  })
}

export const hasGeoConsent = (permissionsToCheck) => {
  const permissions = permissionsToCheck || getPermissions()

  if (!permissions) {
    // if there is no data in the local storage, we treat this as if user has not opted out
    return true
  }
  return permissions.some(
    (p) =>
      p.purpose === 'sdrn:blocket:processingpurpose:targeted_advertising' &&
      p.data.includes('sdrn:schibsted-media:datacategory:geolocation'),
  )
}

const getPulseId = () => {
  const pulseData = readCookie('_pulse2data')
  return pulseData ? pulseData.split(',')[0] : null
}

export const retrieveAndStoreConsent = async ({
  glimrUrl,
  apiUrl,
  bearerToken,
  userId,
  loggedIn,
}) => {
  const pulseId = getPulseId()
  const options = {}
  let consentUrl
  let id
  if (loggedIn && userId) {
    id = `sdrn:schibsted.com:user:${userId}`
    options.headers = {
      Authorization: `Bearer ${bearerToken}`,
    }
    const path = '/privacy-gateway-be/v1/user/permissions'
    consentUrl = `${apiUrl}${path}`
  } else if (pulseId) {
    id = `sdrn:schibsted.com:user:${pulseId}`
    consentUrl = `${glimrUrl}privacy/v1/anonymous/blocket/user/sdrn:schibsted:environment:${pulseId}/permissions`
  } else {
    return null
  }

  const cachedPermissions = getPermissions(id, { includeStale: false })

  if (cachedPermissions) {
    return cachedPermissions
  }

  try {
    const { permissions } = await loadPermissions(consentUrl, options)

    localStorage.persistValue('userConsents', { id, permissions })

    return permissions
  } catch (e) {
    const stalePermissionsForId = getPermissions(id)

    // if no stale permissions for the id, clear the value to make sure we don't have stale permissions for the
    // wrong id
    if (!stalePermissionsForId) {
      localStorage.clearValue('userConsents')
    }

    return stalePermissionsForId
  }
}

const GLIMR_CLIENT_ID = 'OYGH3SXYFAQR78LVPNQD'

const loadGlimr = () => {
  return new Promise((resolve) =>
    require.ensure(
      [],
      (require) => {
        const glimr = require('glimr-sdk')
        glimr.setTagCacheTimeInSeconds(300)
        // request is cached and does not fire multiple requests to glimr.
        glimr.getTags(GLIMR_CLIENT_ID, (tags, tagMappings) => resolve({ tags, tagMappings }))
      },
      'glimr',
    ),
  )
}

export const loadGlimrTags = async (props) => {
  await retrieveAndStoreConsent(props)
  if (hasGeoConsent()) {
    const glimrTags = await loadGlimr()
    localStorage.persistValue('glimrTags', glimrTags)
  }
}
